/*
 * Copyright (c) 2016-2023 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */
import { listenForAttributeChange } from '../utils/events.js';
import { getActiveElement } from '../utils/focus.js';
/**
 * The TriggerController will track the last known interacted element
 * when the host component was created or shown. This is used for when a component
 * needs to know the element that "triggered" the interaction.
 *
 * Examples include, popover types, closable types and draggable types.
 */
export function triggerable(config = { focus: true }) {
    return (target) => target.addInitializer((instance) => new TriggerController(instance, config));
}
export class TriggerController {
    constructor(host, config = { focus: true }) {
        this.host = host;
        this.config = config;
        this.activeElement = getActiveElement();
        this.host.addController(this);
    }
    get current() {
        return this.host.trigger ? this._current : this.activeElement;
    }
    get prev() {
        return this._prev;
    }
    async hostConnected() {
        this.observer = listenForAttributeChange(this.host, 'hidden', () => {
            const activeElement = getActiveElement();
            if (!this.host.hidden && activeElement) {
                this.activeElement = activeElement;
            }
            else {
                this.focusCurrent();
            }
        });
    }
    hostUpdate() {
        if (this._current !== this.host.trigger) {
            this._prev = this._current;
            this._current = this.host.trigger ? this.host.trigger : this.activeElement;
        }
    }
    hostDisconnected() {
        this.focusCurrent();
        this.observer?.disconnect();
    }
    focusCurrent() {
        if (this.config.focus) {
            this.current?.focus();
        }
    }
}
